import { useState } from 'react'
import {
  AXES,
  DOF_RANGES,
  MASS_RANGES,
  dofEdit,
  massEdit,
  massFraction,
  pointDistance,
  segmentLength,
  type AxisKey,
  type DofKey,
  type PointKey,
  type SegmentKey,
} from '@/domain/hs1'
import { Alert, Button, Card, Table, Td, Th } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { NumberCell, num } from '@/sim/parts'

/**
 * Die übrigen Modellwerte — Freiheiten, Massen, abgeleitete Grenzen.
 *
 * Die Gelenkpunkte stehen nicht hier, sondern im Achsbaum: dort sind sie am
 * Strichmännchen zu bedienen, und ein zweiter Satz Eingabefelder daneben wäre
 * dieselbe Zahl an zwei Stellen.
 */

const SEGMENT_ORDER: SegmentKey[] = [
  'thigh',
  'shank',
  'foot',
  'upperArm',
  'forearm',
  'trunk',
  'head',
  'payload',
]

/** Woraus sich eine Segmentlänge ergibt — für die Herkunftsspalte. */
const DERIVED_FROM: Partial<Record<SegmentKey, [PointKey, PointKey]>> = {
  thigh: ['hipN', 'knee'],
  shank: ['knee', 'ankle'],
}

/* -------------------------------------------------------------------------
   Winkelfreiheiten
   ------------------------------------------------------------------------- */

export function Freedoms() {
  const { base, model, setEdit } = useSimulation()

  return (
    <Card flush title="Winkelfreiheiten">
      <p className="page-lead" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
        Bewegungsbereich und zulässiges Moment <strong>am Gelenk</strong>, nicht am Motor: zwischen
        beiden liegt das Getriebe, und dessen Übersetzung ist Ergebnis der Auslegung, nicht ihre
        Voraussetzung.
      </p>
      <Table>
        <thead>
          <tr>
            <Th>Gelenk</Th>
            <Th>Achse</Th>
            <Th numeric>von</Th>
            <Th numeric>bis</Th>
            <Th numeric>Moment</Th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(model.dofs).flatMap(([key, dof]) =>
            AXES.filter((axis) => dof[axis.key as AxisKey] !== null).map((axis, index) => {
              const freedom = dof[axis.key as AxisKey]!
              return (
                <tr key={`${key}-${axis.key}`}>
                  <Td>{index === 0 ? dof.label : ''}</Td>
                  <Td>
                    {axis.label} <span className="cell-muted">{axis.about}</span>
                  </Td>
                  {(['lo', 'hi', 'moment'] as const).map((field) => (
                    <Td key={field} numeric>
                      <NumberCell
                        value={freedom[field]}
                        base={base.dofs[key as DofKey][axis.key as AxisKey]![field]}
                        min={DOF_RANGES[field].min}
                        max={DOF_RANGES[field].max}
                        step={DOF_RANGES[field].step}
                        unit={DOF_RANGES[field].unit}
                        digits={DOF_RANGES[field].digits}
                        onChange={(next) =>
                          setEdit(dofEdit(key as DofKey, axis.key as AxisKey, field), next)
                        }
                      />
                    </Td>
                  ))}
                </tr>
              )
            }),
          )}
        </tbody>
      </Table>
    </Card>
  )
}

/* -------------------------------------------------------------------------
   Massen
   ------------------------------------------------------------------------- */

export function Masses() {
  const { base, model, simulation, setEdit } = useSimulation()
  const { mass } = simulation

  return (
    <div className="stack">
      {mass.relative !== null && Math.abs(mass.relative) > 0.001 && (
        <Alert tone={Math.abs(mass.relative) > 0.02 ? 'warning' : 'info'} title="Massenabgleich">
          Die Summe der Einzelmassen liegt {num(Math.abs(mass.delta ?? 0), 2)} kg{' '}
          {(mass.delta ?? 0) > 0 ? 'über' : 'unter'} dem Vergleichswert von {mass.target} kg. Die
          Abweichung wird gezeigt, nicht verrechnet — eine Bilanz, die sich selbst korrigiert,
          verbirgt genau den Fehler, den sie finden soll.
        </Alert>
      )}

      <Card flush title="Einzelmassen">
        <Table>
          <thead>
            <tr>
              <Th>Segment</Th>
              <Th numeric>Masse</Th>
              <Th numeric>Anteil</Th>
              <Th numeric>Schwerpunkt</Th>
              <Th numeric>Trägheitsradius</Th>
              <Th numeric>Bezugslänge</Th>
              <Th>Herkunft der Länge</Th>
            </tr>
          </thead>
          <tbody>
            {SEGMENT_ORDER.map((key) => {
              const item = model.masses[key]
              const derived = DERIVED_FROM[key]
              return (
                <tr key={key}>
                  <Td>
                    {item.label}
                    {item.paired && <span className="cell-muted"> · paarig</span>}
                  </Td>
                  <Td numeric>
                    <NumberCell
                      value={item.kg}
                      base={base.masses[key].kg}
                      min={MASS_RANGES.kg.min}
                      max={MASS_RANGES.kg.max}
                      step={MASS_RANGES.kg.step}
                      unit={MASS_RANGES.kg.unit}
                      digits={MASS_RANGES.kg.digits}
                      onChange={(next) => setEdit(massEdit(key, 'kg'), next)}
                    />
                  </Td>
                  <Td numeric>
                    {key === 'payload' ? (
                      <span className="cell-muted">—</span>
                    ) : (
                      `${num(100 * massFraction(model, key), 1)} %`
                    )}
                  </Td>
                  {(['com', 'gyration'] as const).map((field) => (
                    <Td key={field} numeric>
                      <NumberCell
                        value={item[field]}
                        base={base.masses[key][field]}
                        min={MASS_RANGES[field].min}
                        max={MASS_RANGES[field].max}
                        step={MASS_RANGES[field].step}
                        digits={MASS_RANGES[field].digits}
                        onChange={(next) => setEdit(massEdit(key, field), next)}
                      />
                    </Td>
                  ))}
                  <Td numeric>{num(1000 * segmentLength(model, key), 0)} mm</Td>
                  <Td>
                    {derived ? (
                      <span className="cell-muted">
                        {model.points[derived[0]].label} → {model.points[derived[1]].label}:{' '}
                        {num(1000 * pointDistance(model, derived[0], derived[1]), 0)} mm
                      </span>
                    ) : (
                      <span className="cell-muted">anthropometrisch, fest</span>
                    )}
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Abgeleitete Grenzen
   ------------------------------------------------------------------------- */

export function Limits() {
  const { simulation } = useSimulation()
  const { limits } = simulation
  const [showSource, setShowSource] = useState(true)

  return (
    <Card
      flush
      title="Grenzen je Gelenk und Ebene"
      actions={
        <Button variant="ghost" size="sm" onClick={() => setShowSource((v) => !v)}>
          {showSource ? 'Herkunft ausblenden' : 'Herkunft zeigen'}
        </Button>
      }
    >
      <p className="page-lead" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
        Aus den Winkelfreiheiten gerechnet, nicht zweitgepflegt.{' '}
        <strong>Achtung Vorzeichen:</strong> die Winkelfreiheiten zählen positiv nach aussen, die
        aufgezeichneten Kurven positiv nach innen. Für die Frontalachsen wird deshalb gespiegelt —
        aus lo … hi wird −hi … −lo.
      </p>
      <Table>
        <thead>
          <tr>
            <Th>Gelenk</Th>
            <Th>Ebene</Th>
            <Th numeric>von</Th>
            <Th numeric>bis</Th>
            <Th numeric>Moment</Th>
            {showSource && <Th>Herkunft</Th>}
          </tr>
        </thead>
        <tbody>
          {Object.entries(limits).flatMap(([joint, planes]) =>
            Object.entries(planes).map(([plane, limit]) => (
              <tr key={`${joint}-${plane}`}>
                <Td>{joint}</Td>
                <Td>{plane}</Td>
                <Td numeric>{limit!.lo}°</Td>
                <Td numeric>{limit!.hi}°</Td>
                <Td numeric>{limit!.moment} Nm</Td>
                {showSource && (
                  <Td>
                    <span className="cell-muted">{limit!.source}</span>
                  </Td>
                )}
              </tr>
            )),
          )}
        </tbody>
      </Table>
    </Card>
  )
}
